/**
 * The only place in the codebase that renders a currency.
 *
 * Everything a money figure needs to be honest is here and nowhere else: the
 * glyph rule, the period lexicon, the scope line, the comparison's
 * relationship word, the discount's provenance, and the stated absence. A
 * pillar that wants any of them gets them by passing data; a pillar that wants
 * to write `$` gets a type error, because the models carry `Money`, not text.
 *
 * The scope line is not decoration. `round trip · 1 adult`,
 * `3 nights · 2 adults · tax incl.` and `catalogue band · floor not
 * purchasable` are the difference between a number and an answer — and where
 * the scope is genuinely unknown the line is OMITTED, never guessed.
 *
 * `layout` decides where the parts go and nothing else. `Baseline` puts the
 * strike, the figure, the period and the discount chip on one baseline, which
 * is right wherever the figure has the width. `Stacked` is the block the row
 * and rail compositions ask for: the figure alone, the period joining the unit
 * line beneath it, the struck original and its discount on a third line. It is
 * the same six facts either way — the formatting is `cresultcard.money.ts`'s
 * and neither layout touches it.
 */
"use client";
import type { ReactElement } from "react";
import {
  currencyGloss,
  formatCresultcardPrice,
  isMoneyText,
  PRICE_BASIS_LABEL,
  PRICE_PERIOD_LABEL,
  type CresultcardPrice,
  type Money,
  type MoneyRange,
} from "./cresultcard.money";
import {
  CRC_FIGURE_SIZE,
  CRC_MONEY_SUFFIX_INK,
  CRC_NUMERIC_STYLE,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardAlign,
  CresultcardFigureScale,
  CresultcardMoneyLayout,
  CresultcardPriceBasis,
  CresultcardPricePeriod,
  CresultcardTone,
} from "./cresultcard.types";

const PERIOD_SIZE_RATIO = 0.44;
const PERIOD_MIN_SIZE = 9;
const SCOPE_SEPARATOR = " · ";
const DISCOUNT_PREFIX = "−";
const DISCOUNT_SUFFIX = "%";
/** A vendor-stated percentage and one we derived are not the same claim. */
const DERIVED_DISCOUNT_TITLE = "derived from the struck price";
const STATED_DISCOUNT_TITLE = "stated by the vendor";

export interface CresultcardMoneyCompare {
  /** The relationship word. A comparison figure is never bare. */
  readonly label: string;
  readonly value: Money | MoneyRange;
}

export interface CresultcardMoneyStrike {
  readonly value: Money;
  readonly discount?: { readonly percent: number; readonly computed: boolean };
}

interface CresultcardMoneyProps {
  readonly price: CresultcardPrice | null;
  readonly period?: CresultcardPricePeriod;
  /**
   * Whether TAX is inside the figure, and nothing else.
   *
   * It is named for the thing it answers because `basis` on its own is a word
   * three different lanes mean three different things by: a hotel's basis is
   * tax, a salary's basis is who published the number, a commodity's basis is
   * the delivery point. One prop absorbing all three would print `tax incl.`
   * under a figure whose lane never said anything about tax. A lane whose basis
   * is a SOURCE states it in `scope`, which is the caller's own words, until the
   * kit is given a ratified source vocabulary of its own.
   */
  readonly taxBasis?: CresultcardPriceBasis;
  /** What the money buys. Omitted entirely when the capture does not say. */
  readonly scope?: string;
  readonly compare?: CresultcardMoneyCompare;
  readonly strike?: CresultcardMoneyStrike;
  readonly scale: CresultcardFigureScale;
  readonly align?: CresultcardAlign;
  /** One baseline, or the stacked block. `Baseline` is what every row draws today. */
  readonly layout?: CresultcardMoneyLayout;
  readonly tone?: CresultcardTone;
}

function compareText(
  compare: CresultcardMoneyCompare | undefined,
): string | null {
  if (typeof compare?.label !== "string" || compare.label.trim().length === 0)
    return null;
  const figure = formatCresultcardPrice(compare.value);
  return figure.length === 0 ? null : `${compare.label} ${figure}`;
}

export function CresultcardMoney({
  price,
  period,
  taxBasis = CresultcardPriceBasis.Unknown,
  scope,
  compare,
  strike,
  scale,
  align = CresultcardAlign.End,
  layout = CresultcardMoneyLayout.Baseline,
  tone = CresultcardTone.Positive,
}: CresultcardMoneyProps): ReactElement | null {
  const alignEnd = align === CresultcardAlign.End;
  const stacked = layout === CresultcardMoneyLayout.Stacked;

  if (price == null) return null;

  const size =
    CRC_FIGURE_SIZE[
      Object.values(CresultcardFigureScale).includes(scale)
        ? scale
        : CresultcardFigureScale.Card
    ];
  const figure = formatCresultcardPrice(price);
  if (figure.length === 0) return null;
  const gloss = isMoneyText(price) ? undefined : currencyGloss(price.currency);
  const basisLabel = PRICE_BASIS_LABEL[taxBasis];
  /** Stacked hands the period to the unit line; Baseline keeps it on the figure. */
  const statedPeriod = period == null ? null : PRICE_PERIOD_LABEL[period];
  const validPeriod = typeof statedPeriod === "string" ? statedPeriod : null;
  const periodLabel = stacked ? validPeriod : null;
  const scopeParts = [scope, basisLabel].filter(
    (part): part is string =>
      typeof part === "string" && part.trim().length > 0,
  );

  const comparison = compareText(compare);
  const struck = formatCresultcardPrice(strike?.value);
  const discount = strike?.discount;
  const validDiscount =
    struck.length > 0 &&
    typeof discount?.percent === "number" &&
    Number.isFinite(discount.percent) &&
    discount.percent >= 0 &&
    discount.percent <= 100 &&
    typeof discount.computed === "boolean"
      ? discount
      : null;

  const strikeFigure =
    struck.length === 0 ? null : (
      <span
        style={{
          ...CRC_NUMERIC_STYLE,
          fontSize: Math.max(PERIOD_MIN_SIZE, Math.round(size * 0.5)),
          fontWeight: 560,
          textDecorationLine: "line-through",
          color: CRC_TEXT_FAINT,
        }}
      >
        {struck}
      </span>
    );

  const discountBadge =
    validDiscount === null ? null : (
      <span
        style={{
          ...CRC_NUMERIC_STYLE,
          paddingBlock: 2,
          paddingInline: 6,
          borderRadius: 999,
          fontSize: 9.5,
          fontWeight: 700,
          lineHeight: 1,
          color: CRC_TONE_COLOR[CresultcardTone.Positive],
          backgroundColor: "rgba(102,254,193,0.1)",
          boxShadow: validDiscount.computed
            ? "inset 0 0 0 1px rgba(102,254,193,0.2)"
            : "inset 0 0 0 1px rgba(102,254,193,0.42)",
        }}
        title={
          validDiscount.computed
            ? DERIVED_DISCOUNT_TITLE
            : STATED_DISCOUNT_TITLE
        }
      >
        {`${DISCOUNT_PREFIX}${String(validDiscount.percent)}${DISCOUNT_SUFFIX}`}
      </span>
    );

  return (
    <span
      data-el="Cresultcard/Money/Root"
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: alignEnd ? "flex-end" : "flex-start",
        gap: 3,
        minWidth: 0,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 5,
          minWidth: 0,
        }}
      >
        {!stacked && strikeFigure}
        <span
          style={{
            ...CRC_NUMERIC_STYLE,
            fontSize: size,
            fontWeight: 800,
            letterSpacing: "-0.035em",
            lineHeight: 1,
            whiteSpace: "nowrap",
            color:
              CRC_TONE_COLOR[tone] ?? CRC_TONE_COLOR[CresultcardTone.Positive],
          }}
          title={gloss}
        >
          {figure}
        </span>
        {!stacked && validPeriod != null && (
          <span
            style={{
              fontSize: Math.max(
                PERIOD_MIN_SIZE,
                Math.round(size * PERIOD_SIZE_RATIO),
              ),
              fontWeight: 560,
              lineHeight: 1,
              whiteSpace: "nowrap",
              color: CRC_MONEY_SUFFIX_INK,
            }}
          >
            {validPeriod}
          </span>
        )}
        {!stacked && discountBadge}
      </span>

      {(periodLabel != null || scopeParts.length > 0) && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 540,
            lineHeight: 1.25,
            textAlign: alignEnd ? "end" : "start",
            color: CRC_TEXT_MUTED,
          }}
        >
          {periodLabel != null && (
            <span style={{ color: CRC_MONEY_SUFFIX_INK }}>{periodLabel}</span>
          )}
          {periodLabel != null && scopeParts.length > 0
            ? SCOPE_SEPARATOR
            : null}
          {scopeParts.join(SCOPE_SEPARATOR)}
        </span>
      )}

      {stacked && strikeFigure != null && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: 5,
            minWidth: 0,
          }}
        >
          {strikeFigure}
          {discountBadge}
        </span>
      )}

      {comparison != null && (
        <span
          style={{
            ...CRC_NUMERIC_STYLE,
            fontSize: 9.5,
            fontWeight: 560,
            lineHeight: 1.25,
            color: CRC_TEXT_FAINT,
          }}
        >
          {comparison}
        </span>
      )}
    </span>
  );
}
